import { T } from '@agos/shared'

import { messageTextLabel, smsSegments, type MessageTextKey } from './campaigns'

/**
 * One of a campaign's texts on its card - `FRONTEND.md` §9.777, and §9.780 for a send by hand.
 *
 * Shows what would be sent until somebody writes their own, with *Pôvodný text* back to it. The order's
 * texts and the by-hand ones carry different markers, so a card with both is never read as one set.
 */
export function MessageTextBox({
  textKey,
  value,
  fallback,
  writable,
  byHand,
  onChange,
}: {
  textKey: MessageTextKey
  value: string
  /** What would be sent with nothing written - the server's resolved default. */
  fallback: string
  writable: boolean
  /** §9.780 - a text of the send by hand rather than of the order. */
  byHand: boolean
  onChange: (text: string) => void
}) {
  const long = textKey === 'pushBody' || textKey === 'emailMessage' || textKey === 'smsText'
  const box = 'w-full rounded-lg border border-border bg-background px-2 text-xs'
  return (
    <label className="block text-[11px] text-muted-foreground space-y-0.5">
      <span className="flex items-center justify-between gap-2">
        <span className="font-semibold">{messageTextLabel(textKey)}</span>
        {/* Back to what would be sent with nothing written - saved as a blank, which the server reads as
          * the default. */}
        {fallback !== '' && value !== fallback && (
          <button
            type="button"
            data-message-reset={byHand ? undefined : textKey}
            data-by-hand-reset={byHand ? textKey : undefined}
            disabled={!writable}
            onClick={() => onChange(fallback)}
            className="text-[10px] underline disabled:opacity-50"
          >
            <T>Pôvodný text</T>
          </button>
        )}
      </span>
      {long ? (
        <textarea
          data-message-text={byHand ? undefined : textKey}
          data-by-hand-text={byHand ? textKey : undefined}
          rows={textKey === 'emailMessage' ? 4 : 2}
          value={value}
          disabled={!writable}
          onChange={(event) => onChange(event.target.value)}
          className={`${box} py-1.5 resize-y`}
        />
      ) : (
        <input
          data-message-text={byHand ? undefined : textKey}
          data-by-hand-text={byHand ? textKey : undefined}
          value={value}
          disabled={!writable}
          onChange={(event) => onChange(event.target.value)}
          className={`${box} min-h-[32px]`}
        />
      )}
      {textKey === 'smsText' && (
        <span
          className="block text-[10px]"
          data-sms-length={byHand ? undefined : smsSegments(value)}
          data-by-hand-sms-length={byHand ? smsSegments(value) : undefined}
        >
          {value.length} <T>znakov</T> · {smsSegments(value)} SMS <T>(pred doplnením mena a kupónu)</T>
        </span>
      )}
    </label>
  )
}
